import { Controller } from "@hotwired/stimulus"

// Signature pad controller for digital signature capture
// Connects to data-controller="signature-pad"
export default class extends Controller {
  static targets = ["canvas", "input", "clearBtn", "status"]

  connect() {
    this.canvas = this.canvasTarget
    this.ctx = this.canvas.getContext("2d")
    this.isDrawing = false
    this.hasSignature = false

    this.setupCanvas()
    this.bindEvents()
  }

  disconnect() {
    this.unbindEvents()
  }

  setupCanvas() {
    const rect = this.canvas.parentElement.getBoundingClientRect()
    this.canvas.width = rect.width
    this.canvas.height = 200

    this.ctx.strokeStyle = "#1a1a2e"
    this.ctx.lineWidth = 2
    this.ctx.lineCap = "round"
    this.ctx.lineJoin = "round"

    // Draw baseline
    this.ctx.setLineDash([5, 5])
    this.ctx.strokeStyle = "#d1d5db"
    this.ctx.beginPath()
    this.ctx.moveTo(20, 160)
    this.ctx.lineTo(this.canvas.width - 20, 160)
    this.ctx.stroke()
    this.ctx.setLineDash([])
    this.ctx.strokeStyle = "#1a1a2e"
  }

  bindEvents() {
    this._mouseDown = this.startDrawing.bind(this)
    this._mouseMove = this.draw.bind(this)
    this._mouseUp = this.stopDrawing.bind(this)
    this._touchStart = this.handleTouchStart.bind(this)
    this._touchMove = this.handleTouchMove.bind(this)
    this._touchEnd = this.stopDrawing.bind(this)

    this.canvas.addEventListener("mousedown", this._mouseDown)
    this.canvas.addEventListener("mousemove", this._mouseMove)
    this.canvas.addEventListener("mouseup", this._mouseUp)
    this.canvas.addEventListener("mouseleave", this._mouseUp)
    this.canvas.addEventListener("touchstart", this._touchStart, { passive: false })
    this.canvas.addEventListener("touchmove", this._touchMove, { passive: false })
    this.canvas.addEventListener("touchend", this._touchEnd)
  }

  unbindEvents() {
    this.canvas.removeEventListener("mousedown", this._mouseDown)
    this.canvas.removeEventListener("mousemove", this._mouseMove)
    this.canvas.removeEventListener("mouseup", this._mouseUp)
    this.canvas.removeEventListener("mouseleave", this._mouseUp)
    this.canvas.removeEventListener("touchstart", this._touchStart)
    this.canvas.removeEventListener("touchmove", this._touchMove)
    this.canvas.removeEventListener("touchend", this._touchEnd)
  }

  startDrawing(e) {
    this.isDrawing = true
    this.ctx.beginPath()
    const pos = this.getPosition(e)
    this.ctx.moveTo(pos.x, pos.y)
  }

  draw(e) {
    if (!this.isDrawing) return
    const pos = this.getPosition(e)
    this.ctx.lineTo(pos.x, pos.y)
    this.ctx.stroke()
    this.hasSignature = true
  }

  stopDrawing() {
    this.isDrawing = false
    if (this.hasSignature) {
      this.updateInput()
      if (this.hasStatusTarget) {
        this.statusTarget.textContent = "Signature captured"
        this.statusTarget.classList.add("text-green-600")
      }
    }
  }

  handleTouchStart(e) {
    e.preventDefault()
    const touch = e.touches[0]
    this.startDrawing(touch)
  }

  handleTouchMove(e) {
    e.preventDefault()
    const touch = e.touches[0]
    this.draw(touch)
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height)
    this.hasSignature = false
    this.setupCanvas()
    if (this.hasInputTarget) this.inputTarget.value = ""
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = "Draw your signature above"
      this.statusTarget.classList.remove("text-green-600")
    }
  }

  getPosition(e) {
    const rect = this.canvas.getBoundingClientRect()
    return {
      x: (e.clientX || e.pageX) - rect.left,
      y: (e.clientY || e.pageY) - rect.top
    }
  }

  updateInput() {
    if (this.hasInputTarget) {
      this.inputTarget.value = this.canvas.toDataURL("image/png")
    }
  }
}
